import { useEffect, useState } from "react";
import {
  Smartphone,
  BadgeCheck,
  QrCode,
  Globe,
  Download,
  Mail,
  KeyRound,
  IdCard,
  Camera,
  Check,
  ArrowRight,
  Minus,
  Plus,
} from "lucide-react";

const TopBar = () => (
  <div className="w-full border-b border-slate-200 bg-white" data-testid="ase-topbar">
    <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3 md:px-8">
      <img
        src="/assets/logo-ase.png"
        alt="Academia de Studii Economice din București"
        className="h-12 w-auto sm:h-14"
        data-testid="ase-topbar-logo"
      />
      <img
        src="/assets/logo-cet.png"
        alt="Cardul European de Tineret - European Youth Card"
        className="h-7 w-auto max-w-[60%] object-contain sm:h-10"
        data-testid="cet-topbar-logo"
      />
    </div>
  </div>
);

const Hero = () => (
  <section className="bg-white pt-14 md:pt-20 pb-6" data-testid="ase-hero">
    <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 md:grid-cols-2 md:gap-8 md:px-8">
      <div>
        <h1 className="text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl">
          Legitimația ta oficială de student ASE, în format digital
        </h1>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
          Începând cu anul universitar 2026-2027, legitimațiile oficiale ale studenților din anul I,
          Licență, Master și Doctorat, ai Academiei de Studii Economice din București vor fi emise în format
          digital și vor fi disponibile direct în aplicația Cardul European de Tineret.
        </p>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
          Legitimația îți confirmă statutul de student ASE și va putea fi utilizată pentru serviciile
          integrate de universitate.
        </p>
      </div>
      <div className="relative flex justify-center">
        <div
          className="absolute inset-0 -z-0 mx-auto my-auto h-80 w-80 rounded-full opacity-70 blur-3xl"
          style={{ backgroundColor: "#2f68b01a" }}
        />
        <img
          src="/assets/ase-phone.png"
          alt="Legitimația digitală de student ASE în aplicație"
          className="relative z-10 h-auto w-[248px] drop-shadow-2xl sm:w-[300px]"
          data-testid="ase-hero-phone"
        />
      </div>
    </div>
  </section>
);

const WHAT_POINTS = [
  {
    icon: Smartphone,
    title: "Legitimația ta, mereu la îndemână",
    text: "O vei avea direct în telefon și o vei putea accesa rapid din aplicație.",
  },
  {
    icon: BadgeCheck,
    title: "Te legitimezi ca student ASE",
    text: "O vei putea utiliza ori de câte ori trebuie să dovedești statutul tău de student ASE.",
  },
  {
    icon: QrCode,
    title: "Cod QR pentru Biblioteca ASE",
    text: "Pe fața legitimației vei găsi codul QR pe care îl vei utiliza la Biblioteca ASE.",
  },
  {
    icon: Globe,
    title: "Parte din ecosistemul European Student Card",
    text: "Legitimația ta va include și elementele European Student Card, inițiativa europeană care sprijină recunoașterea statutului de student între instituțiile de învățământ superior din Europa.",
  },
];

const WhatIs = () => (
  <section className="bg-white py-14 md:py-20" data-testid="ase-what-is">
    <div className="mx-auto max-w-6xl px-5 md:px-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {WHAT_POINTS.map((p, i) => {
          const Icon = p.icon;
          return (
            <div
              key={i}
              className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-colors hover:border-[#2f68b0]/40"
              data-testid={`ase-what-point-${i}`}
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#2f68b0]/10 text-[#2f68b0]">
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <h3 className="mt-4 text-base font-bold text-slate-900">{p.title}</h3>
              <p className="mt-2 text-sm leading-snug text-slate-600">{p.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const STEPS = [
  {
    icon: Download,
    title: "Descarcă aplicația Cardul European de Tineret",
    text: "Aplicația este disponibilă pentru iOS și Android.",
  },
  {
    icon: Mail,
    title: "Așteaptă e-mailul de confirmare",
    text: "Imediat ce legitimația ta ASE este emisă, vei primi un mesaj pe adresa instituțională de e-mail.",
  },
  {
    icon: KeyRound,
    title: "Introdu adresa de e-mail și confirmă codul primit",
    text: "Introdu adresa instituțională de e-mail în aplicație. Vei primi pe această adresă un cod de patru cifre, pe care îl vei introduce în aplicație, apoi îți vei seta parola.",
  },
  {
    icon: IdCard,
    title: "Legitimația ASE apare automat",
    text: "După activarea contului, legitimația ta ASE va fi disponibilă direct în aplicație.",
  },
];

const HowTo = () => (
  <section className="bg-[#f6f9fc] py-16 md:py-24" data-testid="ase-how-to">
    <div className="mx-auto max-w-6xl px-5 md:px-8">
      <h2 className="max-w-2xl text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
        Cum îți vei accesa legitimația ASE
      </h2>
      <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600 md:text-lg">
        Legitimațiile vor fi emise în cursul lunii octombrie 2026. După emitere, vei primi un e-mail
        pe adresa ta instituțională ASE, cu toate informațiile de care ai nevoie. Nu va trebui să
        completezi nicio cerere nouă.
      </p>
      <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={i}
              className="relative rounded-2xl border border-slate-200 bg-white p-7"
              data-testid={`ase-step-${i}`}
            >
              <span className="absolute right-6 top-5 text-5xl font-extrabold text-slate-100 select-none">
                {i + 1}
              </span>
              <span
                className="flex h-12 w-12 items-center justify-center rounded-xl text-white"
                style={{ backgroundColor: "#2f68b0" }}
              >
                <Icon className="h-6 w-6" aria-hidden="true" />
              </span>
              <h3 className="mt-5 text-base font-bold leading-snug text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
              {i === 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="ase-store-badges">
                  <a
                    href="https://apps.apple.com/us/app/cardul-european-de-tineret/id6799863342"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block rounded-lg transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2f68b0] focus-visible:ring-offset-2"
                    data-testid="ase-store-app-store"
                  >
                    <img
                      src="/assets/badge-app-store.svg"
                      alt="Descarcă din App Store"
                      className="h-9 w-auto"
                    />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=ro.isic.cet"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block rounded-lg transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2f68b0] focus-visible:ring-offset-2"
                    data-testid="ase-store-google-play"
                  >
                    <img
                      src="/assets/badge-google-play.png"
                      alt="Descarcă de pe Google Play"
                      className="h-9 w-auto"
                    />
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-6 flex items-start gap-2 text-sm text-slate-500">
        <Camera className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        La prima autentificare, aplicația îți va solicita să adaugi o fotografie tip buletin sau un
        selfie clar.
      </p>
    </div>
  </section>
);

const OptionsIntro = () => (
  <section
    className="py-16 md:py-24"
    style={{ backgroundColor: "#16234a" }}
    data-testid="ase-options-intro"
  >
    <div className="mx-auto max-w-3xl px-5 text-center md:px-8">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7fb0ea]">
        O identitate europeană de student
      </span>
      <h2 className="mt-4 text-3xl font-bold tracking-tight text-white md:text-4xl">
        Student la ASE. Student oriunde în lume.
      </h2>
      <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
        După ce ai legitimația ASE în aplicație, poți să-ți completezi experiența de student și să
        alegi una dintre opțiunile de mai jos sau chiar pe amândouă. Poți obține Cardul European de
        Tineret în condiții speciale și poți aplica gratuit pentru BRD-ISIC.
      </p>
    </div>
  </section>
);

const CET_POINTS = [
  "Beneficii, reduceri și oportunități pentru tineri în România și în 34 de țări europene.",
  "Oferte care te pot ajuta să economisești în călătorii, cumpărături, educație, cultură, sport și viața de zi cu zi.",
  "Acces la campanii, concursuri, experiențe și oportunități dedicate tinerilor.",
  "Activare directă în aplicația în care este disponibilă și legitimația ASE.",
  "După activare, Cardul European de Tineret apare separat, alături de legitimația ASE.",
  "Cotizație specială pentru studenții ASE: 30 lei pentru 12 luni, față de cotizația standard de 50 lei.",
  "Cardul European de Tineret face parte din rețeaua European Youth Card Association, dezvoltată în parteneriat cu instituții și organizații europene, inclusiv Comisia Europeană și Consiliul Europei.",
];

const BRD_POINTS = [
  "Card de debit BRD și legitimație internațională de student într-un singur produs.",
  "Acces la beneficii și reduceri ISIC în România și la nivel internațional.",
  "ISIC este singura legitimație de student acceptată la nivel internațional.",
  "Programul ISIC este susținut de UNESCO din 1968.",
  "Cardul BRD-ISIC este disponibil gratuit pentru studenții eligibili, în condițiile ofertei BRD.",
  "Pentru BRD-ISIC va trebui să descarci o aplicație separată.",
  "Vei primi toate informațiile necesare pe parcursul procesului de emitere a cardului.",
  "Poți parcurge rapid procesul de emitere cu ajutorul echipelor BRD-ISIC prezente în universitate.",
];

const PointList = ({ items }) => (
  <ul className="mt-6 flex-1 space-y-3">
    {items.map((t, i) => (
      <li key={i} className="flex items-start gap-2.5">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2f68b0]" aria-hidden="true" />
        <span className="text-sm leading-snug text-slate-600">{t}</span>
      </li>
    ))}
  </ul>
);

const Options = () => (
  <section className="bg-white py-16 md:py-24" data-testid="ase-options">
    <div className="mx-auto max-w-6xl px-5 md:px-8">
      <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-2">
        <div
          className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-8"
          data-testid="ase-option-cet"
        >
          <div className="flex h-10 items-center">
            <img src="/assets/logo-cet.png" alt="Cardul European de Tineret" className="h-7 w-auto" />
          </div>
          <h3 className="mt-6 text-2xl font-bold text-slate-900">Cardul European de Tineret</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Activează Cardul European de Tineret direct din aplicația în care ai și legitimația ASE și
            intră într-o comunitate europeană dedicată tinerilor.
          </p>
          <PointList items={CET_POINTS} />
          <a
            href="https://carduldetineret.ro/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2f68b0] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#255492]"
            data-testid="ase-option-cet-cta"
          >
            Descoperă Cardul European de Tineret <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>

        <div
          className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-8"
          data-testid="ase-option-brd"
        >
          <div className="flex h-10 items-center gap-3">
            <img src="/assets/logo-brd.webp" alt="BRD" className="h-8 w-auto" />
            <span className="h-8 w-px bg-slate-200" />
            <img src="/assets/logo-isic.png" alt="ISIC" className="h-6 w-auto" />
          </div>
          <p className="mt-2 text-xs font-medium text-slate-400">International Student Identity Card</p>
          <h3 className="mt-4 text-2xl font-bold text-slate-900">BRD-ISIC</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Aplică gratuit pentru BRD-ISIC și ai într-un singur card funcționalitățile unui card de
            debit BRD și beneficiile internaționale ISIC.
          </p>
          <PointList items={BRD_POINTS} />
          <a
            href="https://brd.isic.ro/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2f68b0] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#255492]"
            data-testid="ase-option-brd-cta"
          >
            Descoperă BRD-ISIC <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </div>
      <p className="mt-6 text-center text-sm text-slate-500" data-testid="ase-options-note">
        Poți alege una dintre opțiuni, le poți avea pe amândouă sau poți utiliza doar legitimația ASE.
      </p>
    </div>
  </section>
);

const FAQ_ITEMS = [
  {
    q: "Cine primește legitimația oficială ASE?",
    a: "Legitimația este disponibilă pentru studenții din anul I, Licență, Master și Doctorat, înmatriculați la Academia de Studii Economice din București în anul universitar 2026-2027.",
  },
  { q: "Cât costă legitimația ASE?", a: "Legitimația oficială ASE este oferită gratuit." },
  {
    q: "Când va fi emisă legitimația mea?",
    a: "Legitimațiile vor fi emise în cursul lunii octombrie 2026. După emitere, vei primi toate informațiile necesare pe adresa instituțională de e-mail ASE.",
  },
  {
    q: "Trebuie să completez o cerere?",
    a: "Nu. Nu va trebui să completezi o cerere nouă pentru emiterea legitimației ASE.",
  },
  {
    q: "Unde găsesc legitimația ASE?",
    a: "Legitimația este disponibilă în format digital, în aplicația Cardul European de Tineret.",
  },
  {
    q: "Trebuie să încarc o fotografie?",
    a: "Da. La prima autentificare, aplicația îți va solicita să adaugi o fotografie tip buletin sau un selfie clar.",
  },
  {
    q: "Unde utilizez codul QR?",
    a: "Codul QR de pe fața legitimației va fi utilizat la Biblioteca ASE.",
  },
  {
    q: "Legitimația ASE include beneficii și reduceri?",
    a: "Legitimația ASE este destinată identificării și serviciilor integrate de universitate. Pentru beneficii, reduceri și oportunități suplimentare, poți activa Cardul European de Tineret și/sau poți aplica pentru BRD-ISIC.",
  },
  {
    q: "Cardul European de Tineret și BRD-ISIC sunt obligatorii?",
    a: "Nu. Ambele sunt opționale. Poți alege una dintre ele, le poți avea pe amândouă sau poți utiliza doar legitimația ASE.",
  },
  {
    q: "Pot avea și Cardul European de Tineret, și BRD-ISIC?",
    a: "Da. Cele două oferă beneficii diferite și pot fi utilizate în paralel.",
  },
  {
    q: "Cardul European de Tineret înlocuiește legitimația ASE?",
    a: "Nu. Este o legitimație separată, care apare în aceeași aplicație, alături de legitimația ASE.",
  },
  {
    q: "BRD-ISIC înlocuiește legitimația ASE?",
    a: "Nu. BRD-ISIC este o legitimație internațională de student și un card de debit separat.",
  },
  {
    q: "În ce aplicație găsesc fiecare legitimație?",
    a: "Legitimația ASE și Cardul European de Tineret sunt disponibile în aplicația Cardul European de Tineret. BRD-ISIC este disponibil separat, într-o altă aplicație, iar informațiile necesare îți vor fi oferite în cadrul procesului de emitere.",
  },
];

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="bg-white py-16 md:py-24" data-testid="ase-faq">
      <div className="mx-auto max-w-3xl px-5 md:px-8">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
          Întrebări frecvente
        </h2>
        <div className="mt-10 divide-y divide-slate-200 border-y border-slate-200">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={i} data-testid={`ase-faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="flex w-full items-start justify-between gap-4 py-5 text-left"
                  data-testid={`ase-faq-trigger-${i}`}
                  aria-expanded={isOpen}
                >
                  <span className="text-base font-semibold text-slate-900 md:text-lg">{item.q}</span>
                  <span className="mt-1 shrink-0 text-[#2f68b0]">
                    {isOpen ? (
                      <Minus className="h-5 w-5" aria-hidden="true" />
                    ) : (
                      <Plus className="h-5 w-5" aria-hidden="true" />
                    )}
                  </span>
                </button>
                {isOpen && (
                  <p
                    className="pb-5 text-sm leading-relaxed text-slate-600 md:text-base"
                    data-testid={`ase-faq-answer-${i}`}
                  >
                    {item.a}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default function AsePage() {
  useEffect(() => {
    document.title = "Legitimația oficială de student ASE";
  }, []);

  return (
    <main className="min-h-screen bg-white" data-testid="ase-page">
      <TopBar />
      <Hero />
      <WhatIs />
      <HowTo />
      <OptionsIntro />
      <Options />
      <Faq />
    </main>
  );
}
